import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Menu, X } from 'lucide-react';

const S_PATH = "M64,8 C8,8 8,40 40,40 C72,40 72,72 16,72";
const DOTS: [number, number][] = [
  [64, 8], [32, 13], [19, 24], [22, 35], [40, 40],
  [58, 45], [61, 56], [48, 67], [16, 72],
];
const DOT_R = [4.5, 3.5, 3.5, 3.5, 6.5, 3.5, 3.5, 3.5, 4.5];

function TraceMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true">
      <path d={S_PATH} stroke="#FFA100" strokeWidth="2" fill="none" />
      {DOTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={DOT_R[i]}
          fill={i === 4 ? "#FFA100" : "#ffffff"} />
      ))}
    </svg>
  );
}

interface NavbarProps {
  isHomePage?: boolean;
}

const NAV_ITEMS = [
  { label: 'Home', id: 'home' },
  { label: 'About Us', id: 'about' },
  { label: 'Services', id: 'Services' },
  { label: 'Products', id: 'products' },
  { label: 'Why Choose Us', id: 'ChooseUs' },
  { label: 'Contact', id: 'contact' },
];

export default function Navbar({ isHomePage = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (isHomePage) {
        const sections = ['home', 'about', 'Services', 'products', 'ChooseUs', 'contact'];
        const scrollPosition = window.scrollY + 100;

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetBottom = offsetTop + element.offsetHeight;
            
            if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const scrollToSection = (sectionId: string) => {
    window.dispatchEvent(new CustomEvent('preload-sections'));

    const scroll = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.offsetTop - offset;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth',
        });
      }
    };

    // Allow lazy sections to mount after nav click before scrolling.
    requestAnimationFrame(() => requestAnimationFrame(scroll));
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-[#FFA100] focus:text-white"
      >
        Skip to main content
      </a>
      <nav 
        className={`fixed top-0 left-0 right-0 z-40 bg-[#212529] transition-all duration-300 ${
          isScrolled ? 'py-3' : 'py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <TraceMark size={36} />
              <div className="leading-none">
                <div className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
                  Software <span className="text-[#FFA100]">Lab</span>
                </div>
                <div className="text-gray-500 text-[10px] tracking-widest uppercase" style={{ letterSpacing: '0.12em' }}>
                  SYSTEMS &amp; SOLUTIONS
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            {isHomePage ? (
              <div className="hidden md:flex items-center space-x-8">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`uppercase text-sm transition-all duration-300 border-b-2 pb-1 ${
                      activeSection === item.id
                        ? 'text-[#FFA100] border-[#FFA100]'
                        : 'text-gray-300 border-transparent hover:text-white hover:border-[#FFA100]'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="hidden md:flex items-center">
                <Link
                  to="/"
                  className="uppercase text-sm text-gray-300 hover:text-white hover:border-b-2 hover:border-[#FFA100] pb-1 transition-all duration-300"
                >
                  Home
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4">
              {isHomePage ? (
                <div className="flex flex-col space-y-3">
                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`uppercase text-sm text-left py-2 transition-colors ${
                        activeSection === item.id
                          ? 'text-[#FFA100]'
                          : 'text-gray-300 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : (
                <Link
                  to="/"
                  className="uppercase text-sm text-gray-300 hover:text-white block py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
